import {
  AdaptiveBlurQuality,
  BLUR_FRAME_BUDGET_MS as FRAME_BUDGET_MS,
  BLUR_MAX_FPS as MAX_FRAMES_PER_SECOND,
} from './adaptive-blur-quality';
import {
  backgroundBlurAbortError,
  SoftwareBackgroundBlurError,
  throwIfBlurAborted,
} from './background-blur-error';
import { waitForOperation } from './wait-for-operation';
import type {
  BackgroundBlurWorkerRequest,
  BackgroundBlurWorkerResponse,
} from './worker-messages';

export { SoftwareBackgroundBlurError } from './background-blur-error';

const MAX_OUTPUT_WIDTH = 1280;
const MAX_OUTPUT_HEIGHT = 720;
const WORKER_READY_TIMEOUT_MS = 15000;
const MEDIA_TIMEOUT_MS = 5000;

export interface SoftwareBackgroundBlurOutput {
  stream: MediaStream;
  videoTrack: MediaStreamTrack;
}

export class SoftwareBackgroundBlur {
  private quality = new AdaptiveBlurQuality();
  private frameStartedAt = 0;
  private worker: Worker | null = null;
  private workerReadyPromise: Promise<void> | null = null;
  private resolveWorkerReady: (() => void) | null = null;
  private rejectWorkerReady: ((error: Error) => void) | null = null;
  private startAborter: AbortController | null = null;
  private sourceVideo: HTMLVideoElement | null = null;
  private outputCanvas: HTMLCanvasElement | null = null;
  private outputContext: CanvasRenderingContext2D | null = null;
  private outputStream: MediaStream | null = null;
  private outputTrack: MediaStreamTrack | null = null;
  private sourceTrack: MediaStreamTrack | null = null;
  private sourceConstraints: MediaTrackConstraints | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private frameTimeout: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private frameInFlight = false;
  private generation = 0;
  private averageProcessingTimeMs = 0;
  private resolveFirstFrame:
    ((output: SoftwareBackgroundBlurOutput) => void) | null = null;
  private rejectFirstFrame: ((error: Error) => void) | null = null;
  private hasRenderedFrame = false;

  constructor(
    private readonly onRuntimeError: (error: Error) => void,
    private readonly createWorker: () => Worker = () =>
      new Worker(new URL('./background-blur.worker.ts', import.meta.url), {
        name: 'background-blur',
        type: 'module',
      }),
  ) {}

  static isSupported(): boolean {
    // The worker separately checks that its 2D context implements filters.
    return (
      typeof Worker !== 'undefined' &&
      typeof OffscreenCanvas !== 'undefined' &&
      typeof createImageBitmap !== 'undefined' &&
      typeof HTMLCanvasElement !== 'undefined' &&
      typeof HTMLCanvasElement.prototype.captureStream === 'function'
    );
  }

  async start(
    rawStream: MediaStream,
    parentSignal?: AbortSignal,
  ): Promise<SoftwareBackgroundBlurOutput> {
    throwIfBlurAborted(parentSignal);
    // stop() releases ownership synchronously, before restoring constraints.
    const stopped = this.stop();
    const generation = this.generation;
    const aborter = new AbortController();
    this.startAborter = aborter;
    const { signal } = aborter;
    const abort = () => aborter.abort(parentSignal?.reason);
    parentSignal?.addEventListener('abort', abort, { once: true });

    try {
      await waitForOperation(
        stopped,
        signal,
        MEDIA_TIMEOUT_MS,
        'Timed out releasing the previous blur source',
      );
      throwIfBlurAborted(signal);
      if (!SoftwareBackgroundBlur.isSupported()) {
        throw new SoftwareBackgroundBlurError(
          'Background blur is not supported by this browser',
          true,
        );
      }
      const sourceTrack = rawStream.getVideoTracks()[0];
      if (!sourceTrack || sourceTrack.readyState === 'ended') {
        throw new SoftwareBackgroundBlurError(
          'No camera video track is active',
        );
      }

      await waitForOperation(
        this.ensureWorker(),
        signal,
        WORKER_READY_TIMEOUT_MS,
        'Timed out loading the background blur model',
      );
      throwIfBlurAborted(signal);
      this.running = true;
      this.averageProcessingTimeMs = 0;
      this.quality = new AdaptiveBlurQuality();
      this.sourceTrack = sourceTrack;
      this.sourceConstraints = sourceTrack.getConstraints();
      try {
        await waitForOperation(
          sourceTrack.applyConstraints({
            ...this.sourceConstraints,
            frameRate: {
              ideal: MAX_FRAMES_PER_SECOND,
              max: MAX_FRAMES_PER_SECOND,
            },
            height: { ideal: MAX_OUTPUT_HEIGHT, max: MAX_OUTPUT_HEIGHT },
            width: { ideal: MAX_OUTPUT_WIDTH, max: MAX_OUTPUT_WIDTH },
          }),
          signal,
          MEDIA_TIMEOUT_MS,
          'Timed out configuring the blur camera',
        );
      } catch {
        // Downscaling the bitmap is sufficient when the camera rejects sizing.
        throwIfBlurAborted(signal);
      }
      throwIfBlurAborted(signal);

      const sourceVideo = document.createElement('video');
      sourceVideo.autoplay = true;
      sourceVideo.muted = true;
      sourceVideo.playsInline = true;
      sourceVideo.srcObject = new MediaStream([sourceTrack]);
      this.sourceVideo = sourceVideo;
      await waitForOperation(
        sourceVideo.play(),
        signal,
        MEDIA_TIMEOUT_MS,
        'Timed out starting the blur video source',
      );
      await this.waitForSourceVideo(sourceVideo, signal);
      throwIfBlurAborted(signal);

      const settings = sourceTrack.getSettings();
      const size = this.quality.getSize(
        sourceVideo.videoWidth || settings.width || MAX_OUTPUT_WIDTH,
        sourceVideo.videoHeight || settings.height || MAX_OUTPUT_HEIGHT,
      );
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = size.width;
      outputCanvas.height = size.height;
      const outputContext = outputCanvas.getContext('2d');
      if (!outputContext) {
        throw new SoftwareBackgroundBlurError(
          'Could not create the background blur output canvas',
        );
      }
      const outputStream = outputCanvas.captureStream(MAX_FRAMES_PER_SECOND);
      const outputTrack = outputStream.getVideoTracks()[0];
      if (!outputTrack) {
        throw new SoftwareBackgroundBlurError(
          'Could not create the background blur video track',
        );
      }
      rawStream
        .getAudioTracks()
        .forEach((track) => outputStream.addTrack(track));
      this.outputCanvas = outputCanvas;
      this.outputContext = outputContext;
      this.outputStream = outputStream;
      this.outputTrack = outputTrack;

      const firstFrame = new Promise<SoftwareBackgroundBlurOutput>(
        (resolve, reject) => {
          this.resolveFirstFrame = resolve;
          this.rejectFirstFrame = reject;
        },
      );
      this.scheduleNextFrame(0);
      const output = await waitForOperation(
        firstFrame,
        signal,
        MEDIA_TIMEOUT_MS,
        'Timed out waiting for the first blurred frame',
      );
      throwIfBlurAborted(signal);
      return output;
    } catch (error) {
      // A superseded start must never tear down a newer start's resources.
      if (generation === this.generation) {
        this.terminateWorker();
        await this.stop();
      }
      throw error;
    } finally {
      parentSignal?.removeEventListener('abort', abort);
      if (this.startAborter === aborter) this.startAborter = null;
    }
  }

  async stop(): Promise<void> {
    this.generation += 1;
    this.startAborter?.abort(backgroundBlurAbortError());
    this.startAborter = null;
    this.running = false;
    this.frameInFlight = false;
    this.hasRenderedFrame = false;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.clearFrameTimeout();
    if (this.rejectWorkerReady) this.terminateWorker();
    this.rejectFirstFrame?.(backgroundBlurAbortError());
    this.resolveFirstFrame = null;
    this.rejectFirstFrame = null;

    this.outputTrack?.stop();
    this.outputTrack = null;
    this.outputStream = null;
    this.outputCanvas = null;
    this.outputContext = null;
    if (this.sourceVideo) {
      this.sourceVideo.pause();
      this.sourceVideo.srcObject = null;
      this.sourceVideo = null;
    }
    const sourceTrack = this.sourceTrack;
    const sourceConstraints = this.sourceConstraints;
    this.sourceTrack = null;
    this.sourceConstraints = null;
    if (sourceTrack?.readyState === 'live' && sourceConstraints) {
      try {
        // Restore camera sizing without undoing a subsequent native blur change.
        const constraints = { ...sourceTrack.getConstraints() };
        for (const key of ['width', 'height', 'frameRate'] as const) {
          if (sourceConstraints[key] === undefined) delete constraints[key];
          else constraints[key] = sourceConstraints[key];
        }
        const nativeBlur = sourceTrack.getSettings().backgroundBlur;
        if (nativeBlur !== undefined) constraints.backgroundBlur = nativeBlur;
        await waitForOperation(
          sourceTrack.applyConstraints(constraints),
          new AbortController().signal,
          MEDIA_TIMEOUT_MS,
          'Timed out restoring camera constraints',
        );
      } catch {
        // Restoring camera preferences is best effort during teardown.
      }
    }
  }

  async dispose(): Promise<void> {
    const stopped = this.stop();
    this.terminateWorker();
    await stopped;
  }

  private terminateWorker(): void {
    this.rejectWorkerReady?.(backgroundBlurAbortError());
    this.resolveWorkerReady = null;
    this.rejectWorkerReady = null;
    this.workerReadyPromise = null;
    if (this.worker) {
      this.worker.onmessage = null;
      this.worker.onerror = null;
      this.worker.terminate();
      this.worker = null;
    }
  }

  private ensureWorker(): Promise<void> {
    if (this.workerReadyPromise) return this.workerReadyPromise;
    const worker = this.createWorker();
    this.worker = worker;
    worker.onmessage = (event: MessageEvent<BackgroundBlurWorkerResponse>) => {
      if (worker !== this.worker) {
        if (event.data.type === 'frame') event.data.frame.close();
        return;
      }
      this.handleWorkerMessage(event.data);
    };
    worker.onerror = (event) => {
      if (worker !== this.worker) return;
      this.handleError(
        new SoftwareBackgroundBlurError(
          event.message || 'The background blur worker stopped unexpectedly',
        ),
      );
    };
    const ready = new Promise<void>((resolve, reject) => {
      this.resolveWorkerReady = resolve;
      this.rejectWorkerReady = reject;
    });
    this.workerReadyPromise = ready;
    try {
      worker.postMessage({
        type: 'initialize',
      } satisfies BackgroundBlurWorkerRequest);
    } catch (error) {
      this.handleError(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
    return ready;
  }

  private handleWorkerMessage(message: BackgroundBlurWorkerResponse): void {
    if (message.type === 'ready') {
      this.resolveWorkerReady?.();
      this.resolveWorkerReady = null;
      this.rejectWorkerReady = null;
      return;
    }
    if (message.type === 'error') {
      if (
        message.generation !== undefined &&
        message.generation !== this.generation
      )
        return;
      this.handleError(
        new SoftwareBackgroundBlurError(message.message, message.unavailable),
      );
      return;
    }
    if (message.generation !== this.generation || !this.running) {
      message.frame.close();
      return;
    }
    this.frameInFlight = false;
    this.clearFrameTimeout();
    const { width, height } = message.frame;
    try {
      // Resize only when a completed frame arrives, so capture never sees a
      // cleared canvas while the worker is still processing its replacement.
      if (this.outputCanvas) {
        if (this.outputCanvas.width !== message.frame.width)
          this.outputCanvas.width = message.frame.width;
        if (this.outputCanvas.height !== message.frame.height)
          this.outputCanvas.height = message.frame.height;
      }
      this.outputContext?.drawImage(
        message.frame,
        0,
        0,
        this.outputCanvas?.width ?? message.frame.width,
        this.outputCanvas?.height ?? message.frame.height,
      );
    } catch (error) {
      this.handleError(
        error instanceof Error ? error : new Error(String(error)),
      );
      return;
    } finally {
      message.frame.close();
    }
    const durationMs = Math.max(
      message.processingTimeMs,
      performance.now() - this.frameStartedAt,
    );
    this.averageProcessingTimeMs = this.averageProcessingTimeMs
      ? this.averageProcessingTimeMs * 0.8 + durationMs * 0.2
      : durationMs;
    const qualityChange = this.quality.recordFrame(durationMs);
    if (!this.hasRenderedFrame || qualityChange) {
      const nextSize = this.quality.getSize(
        this.sourceVideo?.videoWidth ?? width,
        this.sourceVideo?.videoHeight ?? height,
      );
      console.info('[Background blur] Software video quality', {
        mode: 'software',
        width,
        height,
        maxFps: MAX_FRAMES_PER_SECOND,
        processingMs: Math.round(this.averageProcessingTimeMs),
        reason: qualityChange ?? 'started',
        nextWidth: nextSize.width,
        nextHeight: nextSize.height,
      });
    }
    if (!this.hasRenderedFrame && this.outputStream && this.outputTrack) {
      this.hasRenderedFrame = true;
      this.resolveFirstFrame?.({
        stream: this.outputStream,
        videoTrack: this.outputTrack,
      });
      this.resolveFirstFrame = null;
      this.rejectFirstFrame = null;
    }
    const delay =
      this.averageProcessingTimeMs <= FRAME_BUDGET_MS
        ? Math.max(0, FRAME_BUDGET_MS - durationMs)
        : this.averageProcessingTimeMs;
    this.scheduleNextFrame(delay);
  }

  private handleError(error: Error): void {
    this.clearFrameTimeout();
    if (this.rejectWorkerReady) {
      this.rejectWorkerReady(error);
      this.terminateWorker();
      return;
    }
    if (!this.running) return;
    this.running = false;
    this.frameInFlight = false;
    if (!this.hasRenderedFrame) {
      if (this.rejectFirstFrame) this.rejectFirstFrame(error);
      else this.startAborter?.abort(error);
      return;
    }
    this.onRuntimeError(error);
    this.terminateWorker();
  }

  private clearFrameTimeout(): void {
    if (this.frameTimeout !== null) clearTimeout(this.frameTimeout);
    this.frameTimeout = null;
  }

  private scheduleNextFrame(delayMs: number): void {
    if (!this.running) return;
    this.timer = setTimeout(() => void this.processNextFrame(), delayMs);
  }

  private async processNextFrame(): Promise<void> {
    const sourceVideo = this.sourceVideo;
    const outputCanvas = this.outputCanvas;
    if (!this.running || this.frameInFlight) return;
    if (
      !sourceVideo ||
      !outputCanvas ||
      sourceVideo.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
    ) {
      this.scheduleNextFrame(FRAME_BUDGET_MS);
      return;
    }
    const generation = this.generation;
    this.frameInFlight = true;
    this.frameStartedAt = performance.now();
    this.frameTimeout = setTimeout(() => {
      if (generation === this.generation) {
        this.handleError(
          new SoftwareBackgroundBlurError(
            'Timed out processing a background blur frame',
          ),
        );
      }
    }, MEDIA_TIMEOUT_MS);
    let frame: ImageBitmap | undefined;
    try {
      const size = this.quality.getSize(
        sourceVideo.videoWidth,
        sourceVideo.videoHeight,
      );
      frame = await createImageBitmap(
        sourceVideo,
        0,
        0,
        sourceVideo.videoWidth,
        sourceVideo.videoHeight,
        {
          resizeHeight: size.height,
          resizeQuality: 'high',
          resizeWidth: size.width,
        },
      );
      if (!this.running || generation !== this.generation) {
        frame.close();
        return;
      }
      if (!this.worker)
        throw new SoftwareBackgroundBlurError(
          'The background blur worker is unavailable',
        );
      this.worker.postMessage(
        {
          frame,
          generation,
          timestampMs: performance.now(),
          type: 'process',
        } satisfies BackgroundBlurWorkerRequest,
        [frame],
      );
    } catch (error) {
      frame?.close();
      if (generation !== this.generation || !this.running) return;
      this.handleError(
        new SoftwareBackgroundBlurError(
          `Could not process the camera frame: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
    }
  }

  private async waitForSourceVideo(
    video: HTMLVideoElement,
    signal: AbortSignal,
  ): Promise<void> {
    throwIfBlurAborted(signal);
    if (
      video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      video.videoWidth > 0
    )
      return;
    let handleLoadedData!: () => void;
    let handleError!: () => void;
    const ready = new Promise<void>((resolve, reject) => {
      handleLoadedData = () => {
        if (
          video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
          video.videoWidth > 0
        )
          resolve();
      };
      handleError = () =>
        reject(
          new SoftwareBackgroundBlurError(
            'The camera video element could not be loaded',
          ),
        );
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
    });
    try {
      await waitForOperation(
        ready,
        signal,
        MEDIA_TIMEOUT_MS,
        'Timed out waiting for camera frames',
      );
    } finally {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
    }
  }
}
